import { CZECH_SUFFIXES, STEMMING_PATTERNS, WORD_BOUNDARY_REGEX } from './constants';
import { normalize } from '@/utils/normalize';

// Function to stem Czech words (simplified)
export const stemCzech = (word: string) => {
  return word
    .replace(STEMMING_PATTERNS[0], '') // Basic stemming
    .replace(STEMMING_PATTERNS[1], 'ov'); // Handle verb forms
};

// Function to generate all variants of a stemmed word
export const generateVariants = (stemmedWord: string) => {
  return [stemmedWord, ...CZECH_SUFFIXES.map(suffix => stemmedWord + suffix)];
};

// Custom function for partial word matching
export const customFindChunks = ({
  searchWords,
  textToHighlight,
}: {
  searchWords: string[];
  textToHighlight: string;
}): { start: number; end: number }[] => {
  if (!Array.isArray(searchWords) || typeof textToHighlight !== 'string') {
    throw new Error('Invalid input: searchWords must be an array and textToHighlight must be a string.');
  }

  const normalizedText = normalize(textToHighlight || '').toLowerCase();
  if (!normalizedText) return [];

  return searchWords.flatMap(word => {
    if (typeof word !== 'string' || !word.trim()) return [];

    try {
      const normalizedWord = normalize(word).toLowerCase();
      const stemmedWord = stemCzech(normalizedWord);
      const variants = generateVariants(stemmedWord);

      if (!Array.isArray(variants) || variants.length === 0) return [];

      const matches: { start: number; end: number }[] = [];

      for (const variant of variants) {
        if (!variant) continue;

        let startIndex = 0;
        while ((startIndex = normalizedText.indexOf(variant, startIndex)) !== -1) {
          // Ensure word boundaries
          let wordStart = startIndex;
          while (wordStart > 0 && WORD_BOUNDARY_REGEX.test(normalizedText[wordStart - 1])) {
            wordStart--;
          }
          let wordEnd = startIndex + variant.length;
          while (wordEnd < normalizedText.length && WORD_BOUNDARY_REGEX.test(normalizedText[wordEnd])) {
            wordEnd++;
          }

          // Avoid duplicate entries
          if (!matches.some(match => match.start === wordStart && match.end === wordEnd)) {
            matches.push({ start: wordStart, end: wordEnd });
          }

          startIndex += variant.length;
          if (startIndex >= normalizedText.length) break; // Prevent infinite loops
        }
      }

      return matches;
    } catch (error) {
      console.error('Error processing word:', word, error);
      return [];
    }
  });
};
